import type { FC, ReactNode } from 'react'
import type { MediaSlot as MediaSlotRow } from '@/types/content'

import { AmbientVideo } from '@/components/generals/ambient-video'
import { MediaPlaceholder } from '@/components/generals/media-placeholder'
import { cn } from '@/lib/utils'
import { cms, hasValue } from '@/lib/cms'

interface MediaSlotProps {
    slot?: MediaSlotRow | null
    slotKey: string
    // Classes for the media frame itself: aspect ratio, size, radius
    className?: string
    figureClassName?: string
    mediaClassName?: string
    tone?: 'dark' | 'light'
    videoMode?: 'ambient' | 'player'
    priority?: boolean
    compact?: boolean
    showCaption?: boolean
    captionClassName?: string
}

// Renders one named media slot from the "medien" collection: its video, its image, or,
// while neither has been uploaded, a labelled placeholder with the slot's brief.
export const MediaSlot: FC<MediaSlotProps> = ({
    slot,
    slotKey,
    className,
    figureClassName,
    mediaClassName,
    tone = 'dark',
    videoMode = 'ambient',
    priority = false,
    compact = false,
    showCaption = false,
    captionClassName
}): ReactNode => {
    const kind = slotKey.includes('VIDEO') ? 'video' : 'image'
    const video = slot && hasValue(slot.video) ? slot.video : null
    const image = slot && hasValue(slot.bild) ? slot.bild : null

    let media: ReactNode

    if (video && videoMode === 'player') {
        media = (
            <video
                src={video}
                poster={image ?? undefined}
                controls
                playsInline
                preload='metadata'
                aria-label={slot?.bezeichnung}
                className={cn('absolute inset-0 size-full bg-ink object-cover', mediaClassName)}
            />
        )
    }

    else if (video) {
        media = <AmbientVideo src={video} poster={image} priority={priority} className={mediaClassName} />
    }

    else if (image) {
        media = (
            <img
                src={image}
                alt={slot?.bild_alt || slot?.bezeichnung || ''}
                loading={priority ? 'eager' : 'lazy'}
                decoding='async'
                fetchPriority={priority ? 'high' : undefined}
                className={cn('absolute inset-0 size-full object-cover', mediaClassName)}
            />
        )
    }

    else {
        media = (
            <MediaPlaceholder
                slotKey={slotKey}
                label={slot?.bezeichnung}
                labelCms={cms('medien', slot?.id, 'bezeichnung')}
                ratio={slot?.seitenverhaeltnis}
                ratioCms={cms('medien', slot?.id, 'seitenverhaeltnis')}
                hint={slot?.bildausschnitt}
                hintCms={cms('medien', slot?.id, 'bildausschnitt')}
                kind={kind}
                tone={tone}
                compact={compact}
            />
        )
    }

    return (
        <figure className={cn('flex flex-col gap-3', figureClassName)}>
            <div className={cn('relative overflow-hidden', tone === 'dark' ? 'bg-ink-soft' : 'bg-stone', className)}>
                {media}
            </div>

            {showCaption && slot && hasValue(slot.bildunterschrift) && (
                <figcaption
                    data-mdlfy-cms={cms('medien', slot.id, 'bildunterschrift')}
                    className={cn('type-label', tone === 'dark' ? 'text-ash' : 'text-smoke', captionClassName)}
                >
                    {slot.bildunterschrift}
                </figcaption>
            )}
        </figure>
    )
}
